import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { useNotifications } from '../lib/notifications';
import { useMyRegistrations } from '../lib/useMyRegistrations';
import { expandAllOccurrences } from '../lib/recurrence';
import * as badgesApi from '../api/badges';
import * as pofApi from '../api/proofOfFunds';
import * as eventsApi from '../api/events';
import * as forumApi from '../api/forum';
import * as tasksApi from '../api/tasks';
import * as schedulesApi from '../api/schedules';
import { StatusPill, RelativeTime, formatDate } from '../components/ui';
import { cn, ui } from '../lib/ui';

const DAY_MS = 24 * 60 * 60 * 1000;

// "Next up" events tile: /events is served off the StatusIndex GSI sorted
// ascending by startsAt (src/shared/models/Event.js `listByStatus`), so the
// front of a `published` page is the soonest event — EXCEPT a published
// event whose end time has already passed but was never transitioned to
// `completed` would sort even earlier. A generous limit (matching the
// service's own default page size) keeps a straggler like that from hiding
// the real next event behind the filter below.
const EVENTS_TILE_LIMIT = 50;

// Tasks tile: GET /tasks only filters on a single `status` value (no OR), so
// "open or in-progress" costs two small calls. STATUSES per Tasks.jsx/
// src/shared/models/Task.js: not_started, in_progress, scheduled, pending,
// in_review, on_hold, blocked, completed — "open" here means the two a member
// would recognise as "still to do": not_started and in_progress.
const TASKS_TILE_LIMIT = 25;

// Calendar tile: soonest schedule occurrence in the next 14 days. Masters are
// windowed on their OWN stored startTime (src/services/schedules/handlers.js
// `handleList`), so a recurring weekly meeting created a year ago needs the
// same 366-day look-back Schedules.jsx uses, or its master never matches
// `startAfter` at all. Unlike Schedules.jsx this fetches a single page (no
// `fetchAllPages` loop) — a tile only needs "what's next", not an exhaustive
// calendar, and a member with more than 100 schedule masters touching this
// window is far outside what a dashboard tile is for.
const CALENDAR_TILE_WINDOW_DAYS = 14;
const CALENDAR_TILE_LOOKBACK_DAYS = 366;
const CALENDAR_TILE_LIMIT = 100;

// Review queue tile: GET /proof-of-funds is page-capped (DEFAULT_LIMIT 50,
// src/services/proof-of-funds/index.js), and its `count` is only the page's
// count, never a true total. A `lastEvaluatedKey` on the response means more
// than 50 are waiting, so the tile is honest about it with a "50+" label
// rather than implying 50 is the whole queue.
const REVIEW_QUEUE_TILE_LIMIT = 50;

export default function Dashboard() {
  const { user, isAccredited, isReviewer } = useAuth();
  const { unreadCount } = useNotifications();
  const registrations = useMyRegistrations();

  const [badges, setBadges] = useState(null);
  const [proofs, setProofs] = useState(null);

  const [eventsTile, setEventsTile] = useState({ loading: true, error: false, event: null });
  const [forumTile, setForumTile] = useState({ loading: true, error: false, totalThreads: 0 });
  const [tasksTile, setTasksTile] = useState({
    loading: true,
    error: false,
    openCount: null,
    capped: false,
  });
  const [calendarTile, setCalendarTile] = useState({ loading: true, error: false, next: null });
  const [queueTile, setQueueTile] = useState({
    loading: true,
    error: false,
    count: null,
    capped: false,
  });

  useEffect(() => {
    let cancelled = false;
    // Each card degrades independently — a failed side-load never blanks the page.
    badgesApi
      .listMyBadges()
      .then(d => !cancelled && setBadges(d.badges || []))
      .catch(() => !cancelled && setBadges([]));
    pofApi
      .listMine()
      .then(
        d =>
          !cancelled &&
          setProofs(
            (d.proofs || [])
              .slice()
              .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0))
          )
      )
      .catch(() => !cancelled && setProofs([]));
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    eventsApi
      .listEvents({ limit: EVENTS_TILE_LIMIT })
      .then(d => {
        if (cancelled) return;
        const now = Date.now();
        const upcoming = (d.events || [])
          .filter(event => event.startsAt && new Date(event.startsAt).getTime() >= now)
          .sort((a, b) => new Date(a.startsAt) - new Date(b.startsAt));
        setEventsTile({ loading: false, error: false, event: upcoming[0] || null });
      })
      .catch(() => !cancelled && setEventsTile({ loading: false, error: true, event: null }));
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    forumApi
      .listCategories()
      .then(d => {
        if (cancelled) return;
        const totalThreads = (d.categories || []).reduce(
          (sum, category) => sum + (Number(category.threadCount) || 0),
          0
        );
        setForumTile({ loading: false, error: false, totalThreads });
      })
      .catch(() => !cancelled && setForumTile({ loading: false, error: true, totalThreads: 0 }));
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      tasksApi.listTasks({ status: 'not_started', limit: TASKS_TILE_LIMIT }),
      tasksApi.listTasks({ status: 'in_progress', limit: TASKS_TILE_LIMIT }),
    ])
      .then(([notStarted, inProgress]) => {
        if (cancelled) return;
        const openCount = (notStarted.count || 0) + (inProgress.count || 0);
        const capped = Boolean(notStarted.lastEvaluatedKey || inProgress.lastEvaluatedKey);
        setTasksTile({ loading: false, error: false, openCount, capped });
      })
      .catch(
        () =>
          !cancelled &&
          setTasksTile({ loading: false, error: true, openCount: null, capped: false })
      );
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const rangeStart = new Date();
    const rangeEnd = new Date(rangeStart.getTime() + CALENDAR_TILE_WINDOW_DAYS * DAY_MS);
    const startAfter = new Date(
      rangeStart.getTime() - CALENDAR_TILE_LOOKBACK_DAYS * DAY_MS
    ).toISOString();
    Promise.all([
      schedulesApi
        .listSchedules({
          startAfter,
          startBefore: rangeEnd.toISOString(),
          limit: CALENDAR_TILE_LIMIT,
        })
        .then(d => {
          const occurrences = expandAllOccurrences(d.schedules || [], rangeStart, rangeEnd);
          return occurrences
            .filter(o => o.occurrenceStart.getTime() >= rangeStart.getTime())
            .map(o => ({
              _kind: 'personal',
              subject: o.schedule.subject,
              time: o.occurrenceStart,
              link: `/schedules?select=${encodeURIComponent(o.schedule.id)}`,
            }));
        })
        .catch(() => []),
      eventsApi
        .listEvents({ limit: EVENTS_TILE_LIMIT })
        .then(d => {
          const now = Date.now();
          return (d.events || [])
            .filter(e => e.startsAt && new Date(e.startsAt).getTime() >= now)
            .map(e => ({
              _kind: 'official',
              subject: e.name,
              time: new Date(e.startsAt),
              link: `/events/${encodeURIComponent(e.eventId)}`,
            }));
        })
        .catch(() => []),
    ]).then(([personal, official]) => {
      if (cancelled) return;
      const all = [...personal, ...official].sort((a, b) => a.time.getTime() - b.time.getTime());
      setCalendarTile({ loading: false, error: false, next: all[0] || null });
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!isReviewer) return undefined;
    let cancelled = false;
    pofApi
      .listQueue({ status: 'pending', limit: REVIEW_QUEUE_TILE_LIMIT })
      .then(d => {
        if (cancelled) return;
        setQueueTile({
          loading: false,
          error: false,
          count: d.count ?? (d.proofs || []).length,
          capped: Boolean(d.lastEvaluatedKey),
        });
      })
      .catch(
        () =>
          !cancelled && setQueueTile({ loading: false, error: true, count: null, capped: false })
      );
    return () => {
      cancelled = true;
    };
  }, [isReviewer]);

  const latestProof = proofs && proofs[0];
  const isRegisteredForNextEvent =
    !registrations.loading &&
    eventsTile.event &&
    Boolean(registrations.byEventId[eventsTile.event.eventId]);

  return (
    <>
      <h1 className={ui.h1}>Welcome{user && user.firstName ? `, ${user.firstName}` : ''}</h1>

      <div className={cn(ui.grid, ui.gridMd3)}>
        <section className={ui.card}>
          <h3 className={ui.h3}>Verification status</h3>
          {isAccredited ? (
            <p>
              <StatusPill status="verified" /> You have accredited investor access.
            </p>
          ) : latestProof && latestProof.status === 'expired' ? (
            <p>
              <StatusPill status={latestProof.status} /> Your verification has expired — resubmit.
            </p>
          ) : latestProof ? (
            <p>
              <StatusPill status={latestProof.status} /> Proof of funds{' '}
              {latestProof.status === 'rejected'
                ? 'was rejected'
                : latestProof.status === 'verified'
                  ? 'was approved'
                  : 'is in review'}{' '}
              (submitted {formatDate(latestProof.submittedAt || latestProof.createdAt)}).
            </p>
          ) : (
            <p className={ui.muted}>
              Accredited features are locked until a proof-of-funds review is approved.
            </p>
          )}
          <Link className={ui.btnSecondary} to="/proof-of-funds">
            {isAccredited ? 'View verification' : 'Start verification'}
          </Link>
        </section>

        <section className={ui.card}>
          <h3 className={ui.h3}>Notifications</h3>
          {unreadCount > 0 ? (
            <p>
              You have <strong>{unreadCount}</strong> unread notification
              {unreadCount === 1 ? '' : 's'}.
            </p>
          ) : (
            <p className={ui.muted}>You&rsquo;re all caught up.</p>
          )}
          <Link className={ui.btnSecondary} to="/notifications">
            Open inbox
          </Link>
        </section>

        <section className={ui.card}>
          <h3 className={ui.h3}>Member badges</h3>
          {badges === null ? (
            <p className={ui.muted}>Loading…</p>
          ) : badges.length ? (
            <div className={ui.row}>
              {badges.map(b => (
                <span key={b.badgeId || b.awardId} className={ui.pillNeutral}>
                  {(b.definition && b.definition.name) || b.badgeId}
                </span>
              ))}
            </div>
          ) : (
            <p className={ui.muted}>No badges yet — join events and the community to earn them.</p>
          )}
        </section>
      </div>

      <div className={cn(ui.grid, ui.gridMd3, 'mt-4')}>
        <section className={ui.card}>
          <h3 className={ui.h3}>Member events</h3>
          {eventsTile.loading ? (
            <p className={ui.muted}>Loading…</p>
          ) : eventsTile.error ? (
            <p className={ui.muted}>Couldn&rsquo;t load events.</p>
          ) : eventsTile.event ? (
            <p>
              <Link to={`/events/${encodeURIComponent(eventsTile.event.eventId)}`}>
                {eventsTile.event.name}
              </Link>
              <br />
              <RelativeTime iso={eventsTile.event.startsAt} />
              {eventsTile.event.venue ? ` — ${eventsTile.event.venue}` : ''}
              {isRegisteredForNextEvent && (
                <>
                  <br />
                  <span className="text-body-s">You&rsquo;re registered ✓</span>
                </>
              )}
            </p>
          ) : (
            <p className={ui.muted}>No upcoming events.</p>
          )}
          <Link className={ui.btnSecondary} to="/events">
            See events
          </Link>
        </section>

        <section className={ui.card}>
          <h3 className={ui.h3}>Community forum</h3>
          {forumTile.loading ? (
            <p className={ui.muted}>Loading…</p>
          ) : forumTile.error ? (
            <p className={ui.muted}>Couldn&rsquo;t load forum activity.</p>
          ) : (
            <p>
              <strong>{forumTile.totalThreads}</strong> thread
              {forumTile.totalThreads === 1 ? '' : 's'} across the community forum.
            </p>
          )}
          <Link className={ui.btnSecondary} to="/forum">
            Browse forum
          </Link>
        </section>

        <section className={ui.card}>
          <h3 className={ui.h3}>Tasks</h3>
          {tasksTile.loading ? (
            <p className={ui.muted}>Loading…</p>
          ) : tasksTile.error ? (
            <p className={ui.muted}>Couldn&rsquo;t load your tasks.</p>
          ) : tasksTile.openCount > 0 ? (
            <p>
              <strong>
                {tasksTile.openCount}
                {tasksTile.capped ? '+' : ''}
              </strong>{' '}
              open or in-progress task{tasksTile.openCount === 1 && !tasksTile.capped ? '' : 's'}.
            </p>
          ) : (
            <p className={ui.muted}>No open tasks.</p>
          )}
          <Link className={ui.btnSecondary} to="/tasks">
            Open tasks
          </Link>
        </section>
      </div>

      <div className={cn(ui.grid, isReviewer && ui.gridMd2, 'mt-4')}>
        <section className={ui.card}>
          <h3 className={ui.h3}>Next on your calendar</h3>
          {calendarTile.loading ? (
            <p className={ui.muted}>Loading…</p>
          ) : calendarTile.error ? (
            <p className={ui.muted}>Couldn&rsquo;t load your schedule.</p>
          ) : calendarTile.next ? (
            <p>
              <Link to={calendarTile.next.link}>{calendarTile.next.subject}</Link>
              <br />
              <RelativeTime iso={calendarTile.next.time.toISOString()} />
              {calendarTile.next._kind === 'official' && (
                <span className={cn(ui.pillInfo, 'ml-2 text-body-s!')}>Official</span>
              )}
            </p>
          ) : (
            <p className={ui.muted}>Nothing on your calendar in the next two weeks.</p>
          )}
          <Link className={ui.btnSecondary} to="/schedules">
            Open calendar
          </Link>
        </section>

        {isReviewer && (
          <section className={ui.card}>
            <h3 className={ui.h3}>Review queue</h3>
            {queueTile.loading ? (
              <p className={ui.muted}>Loading…</p>
            ) : queueTile.error ? (
              <p className={ui.muted}>Couldn&rsquo;t load the review queue.</p>
            ) : (
              <p>
                <strong>
                  {queueTile.count}
                  {queueTile.capped ? '+' : ''}
                </strong>{' '}
                submission{queueTile.count === 1 && !queueTile.capped ? '' : 's'} awaiting review.
              </p>
            )}
            <Link className={ui.btnSecondary} to="/review">
              Open review queue
            </Link>
          </section>
        )}
      </div>
    </>
  );
}
